import { Injectable } from '@angular/core';
import {Subject} from "rxjs";

import { WebsocketService } from './websocket.service';
import { ConfigService } from './config.service';
import { AccountService } from './account.service';
import { Message } from '../models/message/message';
import { NewConversationAlert } from '../models/message/new-conversation-alert';
import { MaintenanceIssue } from '../models/maintenance/maintenance-issue';
import { Viewing } from '../models/tenant-find/viewing';
import { Onboarding } from '../models/onboarding/onboarding';
import { AutoRentMatchingUpdate } from '../models/finance/auto-rent-matching-update';
import {TdsDeposit} from "../models/tds/tds-deposit";
import {InventoryTemplate} from "../models/inventory-template/inventory-template.interface";
import {GoCardlessExport} from "../models/gocardless/gocardless-export.interface";
import {OrganisationRentCollectionSettings} from "../models/rent-collection/organisation-rent-collection-settings";
import {GoCardlessRentDueRecord} from "../models/gocardless/go-cardless-rent-due-record";
import {GoCardlessTenancy} from "../models/gocardless/gocardless-tenancy";
import {Lead} from "../models/tenant-find/lead";
import {RightmoveAuthenticationInformation} from "../models/rightmove/rightmove-authentication-information.interface";
import {RightmoveProperty} from "../models/rightmove/rightmove-property.interface";
import {ConversationUser} from "../models/message/conversation-user";
import {OrganisationUser} from "../models/organisation/organisation-user";
import {YourPlaceListing} from "../models/your-place/your-place-listing";
import {ConversationNotificationRequirement} from "../models/message/conversation-notification-requirement";
import {HmrcAuth} from "../models/gov-hmrc/hmrc-auth";
import {OpenBankingConnectionUpdated} from '../models/open-banking/open-banking-connection-updated';

export class ConnectionMethods {
    static MESSAGE_RECEIVED = 'MessageReceived';
    static CONVERSATION_RECEIVED = 'ConversationReceived';
    static REPAIR_UPDATE_RECEIVED = 'RepairUpdateReceived';
    static VIEWING_UPDATE_RECEIVED = 'ViewingUpdateReceived';
    static ONBOARDING_UPDATE_RECEIVED = 'OnboardingUpdateReceived';
    static MANAGER_MAINTENANCE_ISSUE_UPDATE_RECEIVED = 'ManagerMaintenanceIssueUpdateReceived';
    static SUPPLIER_MAINTENANCE_ISSUE_UPDATE_RECEIVED = 'SupplierMaintenanceIssueUpdateReceived';
    static OWNER_MAINTENANCE_ISSUE_UPDATE_RECEIVED = 'OwnerMaintenanceIssueUpdateReceived';
    static TENANT_MAINTENANCE_ISSUE_UPDATE_RECEIVED = 'TenantMaintenanceIssueUpdateReceived';
    static NEW_NOTIFICATIONS_ALERT_RECEIVED = 'NewNotificationsAlert';
    static NEW_MANAGE_ACTIONS_ALERT_RECEIVED = 'NewManageActionsAlert';
    static NEW_MANAGE_NOTIFICATIONS_ALERT_RECEIVED = 'NewManageNotificationsAlert';
    static NEW_OWNER_ACTIONS_ALERT_RECEIVED = 'NewOwnerActionsAlert';
    static NEW_OWNER_NOTIFICATIONS_ALERT_RECEIVED = 'NewOwnerNotificationsAlert';
    static NEW_SUPPLIER_ACTIONS_ALERT_RECEIVED = 'NewSupplierActionsAlert';
    static NEW_SUPPLIER_NOTIFICATIONS_ALERT_RECEIVED = 'NewSupplierNotificationsAlert';
    static NEW_MY_HOME_ACTIONS_ALERT_RECEIVED = 'NewMyHomeActionsAlert';
    static NEW_MY_HOME_NOTIFICATIONS_ALERT_RECEIVED = 'NewMyHomeNotificationsAlert';
    static ARTICLE_READ_ALERT_RECEIVED = 'ArticleRead';
    static AUTO_MATCHING_RENT_DUE_RECORDS_UPDATE = 'AutoMatchingRentDueRecordsUpdate';
    static OpenBankingConnectionUpdated = "OpenBankingConnectionUpdated"
    static TDS_DEPOSIT_UPDATE_RECEIVED = "TdsDepositUpdateReceived"
    static INVENTORY_TEMPLATE_CREATED = 'InventoryTemplateCreated';
    static INVENTORY_TEMPLATE_UPDATED = 'InventoryTemplateUpdated';
    static INVENTORY_TEMPLATE_ARCHIVED = 'InventoryTemplateArchived';
    static GO_CARDLESS_EXPORT_CREATED = 'GoCardlessExportCreated';
    static MESSAGE_DELETED = 'MessageDeleted';
    static MANAGE_ACTIONS_UPDATE_PING = 'ManageActionsUpdatePing';
    static RightmoveAuthUpdated = 'RightmoveAuthUpdated';
    static RightmoveAuthDeleted = 'RightmoveAuthDeleted';
    static RightmovePropertyUpdated = 'RightmovePropertyUpdated';
    static RightmovePropertyDeleted = 'RightmovePropertyDeleted';
    static NewLead = 'NewLead';
    static OrganisationRentCollectionSettingsUpdated = 'OrganisationRentCollectionSettingsUpdated';
    static GoCardlessRentDueRecordUpdated = 'GoCardlessRentDueRecordUpdated';
    static GoCardlessTenancyUpdated = 'GoCardlessTenancyUpdated';
    static ConversationArchivedForUser = 'ConversationArchivedForUser';
    static ConversationReadForUser = 'ConversationReadForUser';
    static ConversationRead = 'ConversationRead';
    static ConversationMutedForUser = 'ConversationMutedForUser';
    static ConversationDeleted = 'ConversationDeleted';
    static ConversationAllowRepliesSwitched = 'ConversationAllowRepliesSwitched';
    static ConversationNewUser = 'ConversationNewUser';
    static ConversationDisabledUser = 'ConversationDisabledUser';
    static ConversationAssigned = 'ConversationAssigned';
    static Inventory = 'Inventory';
    static ConversationNotificationState = 'ConversationNotificationState';
    static YourPlaceListingUpdated = 'YourPlaceListingUpdated';
    static YourPlaceListingDeleted = 'YourPlaceListingDeleted';
    static GovHmrcAuth = 'GovHmrcAuth';
    static RentMatchRuleUpdated = 'RentMatchRuleUpdated';
}

export enum WebsocketEventType {
    Unspecified,
    Create,
    Update,
    Delete

}

@Injectable({
    providedIn: 'root'
})
export class WebsocketNotificationService extends WebsocketService {
    messageReceived = new Subject<Message>();
    conversationReceived = new Subject<NewConversationAlert>();
    repairUpdateReceived = new Subject<MaintenanceIssue>();
    viewingUpdateReceived = new Subject<Viewing>();
    onboardingUpdateReceived = new Subject<Onboarding>();
    managerMaintenanceIssueUpdateReceived = new Subject<MaintenanceIssue>();
    supplierMaintenanceIssueUpdateReceived = new Subject<MaintenanceIssue>();
    ownerMaintenanceIssueUpdateReceived = new Subject<MaintenanceIssue>();
    tenantMaintenanceIssueUpdateReceived = new Subject<MaintenanceIssue>();
    newNotificationsAlertReceived = new Subject<boolean>();
    newManageActionsAlertReceived = new Subject<boolean>();
    newManageNotificationsAlertReceived = new Subject<boolean>();
    newOwnerActionsAlertReceived = new Subject<boolean>();
    newOwnerNotificationsAlertReceived = new Subject<boolean>();
    newSupplierActionsAlertReceived = new Subject<boolean>();
    newSupplierNotificationsAlertReceived = new Subject<boolean>();
    newMyHomeActionsAlertReceived = new Subject<boolean>();
    newMyHomeNotificationsAlertReceived = new Subject<boolean>();
    articleReadReceived = new Subject<string>();
    autoMatchingRentDueRecordsUpdateReceived = new Subject<AutoRentMatchingUpdate>();
    openBankingConnectionUpdated = new Subject<OpenBankingConnectionUpdated>();
    TdsDepositUpdateReceived = new Subject<TdsDeposit>();
    inventoryTemplateCreated = new Subject<InventoryTemplate>();
    inventoryTemplateUpdated = new Subject<InventoryTemplate>();
    inventoryTemplateArchived = new Subject<InventoryTemplate>()
    goCardlessExportCreated = new Subject<GoCardlessExport>()
    messageDeleted = new Subject<Message>();
    manageActionsUpdatePing = new Subject<boolean>();
    organisationRentCollectionSettingsUpdated = new Subject<OrganisationRentCollectionSettings>();
    goCardlessRentDueRecordUpdated = new Subject<GoCardlessRentDueRecord>()
    goCardlessTenancyUpdated = new Subject<GoCardlessTenancy>();
    rightmoveAuthUpdated = new Subject<RightmoveAuthenticationInformation>();
    rightmoveAuthDeleted = new Subject<RightmoveAuthenticationInformation>();
    rightmovePropertyUpdated = new Subject<RightmoveProperty>();
    rightmovePropertyDeleted = new Subject<RightmoveProperty>();
    newLead = new Subject<Lead>();
    conversationArchivedForUser = new Subject<{archived: boolean, reference: string}>()
    conversationReadForUser = new Subject<{read: boolean, reference: string}>()
    conversationRead = new Subject<{conversationUserReference: string, readAt: string, reference: string}>()
    conversationMutedForUser = new Subject<{muted: boolean, reference: string}>()
    conversationDeleted = new Subject<string>()
    conversationAllowRepliesSwitched = new Subject<{allowReplies: boolean, reference: string}>()
    conversationNewUser = new Subject<{ newConversationUser: ConversationUser, reference: string }>()
    conversationDisabledUser = new Subject<{ disabledConversationUserReference: string, reference: string }>()
    conversationAssigned = new Subject<{ assignee: OrganisationUser, reference: string }>()
    inventoryEvent = new Subject<{ eventType: WebsocketEventType, guid: string, tenancyGuid: string }>()
    conversationNotificationState = new Subject<{ notificationRequirement: ConversationNotificationRequirement, notificationCompleted: string, reference: string }>()
    yourPlaceListingUpdated = new Subject<YourPlaceListing>();
    yourPlaceListingDeleted = new Subject<YourPlaceListing>();
    govHmrcAuth = new Subject<{ auth: HmrcAuth }>()
    rentMatchRuleUpdated = new Subject<{ rentMatchRuleGuid: string, operation: string}>()

    fallbackTimeoutSubject = new Subject<{ connectionMethod: ConnectionMethods, triggered: boolean, objectId: string }>(); // Updated subject type
    private fallbackTimeouts: { [key: string]: any } = {}; // Dictionary to track timeouts

    constructor(configService: ConfigService, accountService: AccountService) {
        super(configService, accountService);

        this.createConnection('/hubs/notifications');
        this.registerOnServerEvents();
        this.startConnection();
    }

    private registerOnServerEvents(): void {
        Object.keys(ConnectionMethods).forEach(key => this._hubConnection.off(key));

        this._hubConnection.on(ConnectionMethods.MESSAGE_RECEIVED, (data: Message) => {
            this.messageReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.CONVERSATION_RECEIVED, (data: NewConversationAlert) => {
            this.conversationReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.REPAIR_UPDATE_RECEIVED, (data: MaintenanceIssue) => {
            this.repairUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.VIEWING_UPDATE_RECEIVED, (data: Viewing) => {
            console.log('viewing update received', data)
            this.clearFallbackTimeout(ConnectionMethods.VIEWING_UPDATE_RECEIVED)
            this.viewingUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ONBOARDING_UPDATE_RECEIVED, (data: Onboarding) => {
            this.onboardingUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.MANAGER_MAINTENANCE_ISSUE_UPDATE_RECEIVED, (data: MaintenanceIssue) => {
            this.managerMaintenanceIssueUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.OWNER_MAINTENANCE_ISSUE_UPDATE_RECEIVED, (data: MaintenanceIssue) => {
            this.ownerMaintenanceIssueUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.TENANT_MAINTENANCE_ISSUE_UPDATE_RECEIVED, (data: MaintenanceIssue) => {
            this.tenantMaintenanceIssueUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.SUPPLIER_MAINTENANCE_ISSUE_UPDATE_RECEIVED, (data: MaintenanceIssue) => {
            this.supplierMaintenanceIssueUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_NOTIFICATIONS_ALERT_RECEIVED, (data: boolean) => {
            console.log('newNotificationsAlertReceived', data);
            this.newNotificationsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_MANAGE_ACTIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newManageActionsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.MANAGE_ACTIONS_UPDATE_PING, (data: boolean) => {
            this.manageActionsUpdatePing.next(data);
            console.log('ping action update');
        });

        this._hubConnection.on(ConnectionMethods.NEW_MANAGE_NOTIFICATIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newManageNotificationsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_OWNER_ACTIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newOwnerActionsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_SUPPLIER_ACTIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newSupplierActionsAlertReceived.next(data);
        });
        this._hubConnection.on(ConnectionMethods.NEW_OWNER_NOTIFICATIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newOwnerNotificationsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_SUPPLIER_NOTIFICATIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newSupplierNotificationsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_MY_HOME_ACTIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newMyHomeActionsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NEW_MY_HOME_NOTIFICATIONS_ALERT_RECEIVED, (data: boolean) => {
            this.newMyHomeNotificationsAlertReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ARTICLE_READ_ALERT_RECEIVED, (articleReference: string) => {
            this.articleReadReceived.next(articleReference);
        });

        this._hubConnection.on(ConnectionMethods.AUTO_MATCHING_RENT_DUE_RECORDS_UPDATE, (data: AutoRentMatchingUpdate) => {
            this.autoMatchingRentDueRecordsUpdateReceived.next(data);
        });

        this._hubConnection.on(ConnectionMethods.OpenBankingConnectionUpdated, (data: OpenBankingConnectionUpdated) => {
            this.openBankingConnectionUpdated.next(data);
        })

        this._hubConnection.on(ConnectionMethods.TDS_DEPOSIT_UPDATE_RECEIVED, (data: TdsDeposit) => {
            this.TdsDepositUpdateReceived.next(data);
            this.clearFallbackTimeout(ConnectionMethods.TDS_DEPOSIT_UPDATE_RECEIVED);
        });

        this._hubConnection.on(ConnectionMethods.INVENTORY_TEMPLATE_CREATED, (data: InventoryTemplate) => {
            this.inventoryTemplateCreated.next(data);
        })

        this._hubConnection.on(ConnectionMethods.INVENTORY_TEMPLATE_UPDATED, (data: InventoryTemplate) => {
            this.inventoryTemplateUpdated.next(data);
        })

        this._hubConnection.on(ConnectionMethods.INVENTORY_TEMPLATE_ARCHIVED, (data: InventoryTemplate) => {
            this.inventoryTemplateArchived.next(data);
        })

        this._hubConnection.on(ConnectionMethods.GO_CARDLESS_EXPORT_CREATED, (data: GoCardlessExport) => {
            console.log('goCardlessExportCreated websocket reception', data)
            this.goCardlessExportCreated.next(data);
        })

        this._hubConnection.on(ConnectionMethods.MESSAGE_DELETED, (data: Message) => {
            console.log('message deleted', data)
            this.messageDeleted.next(data);
        });

        this._hubConnection.on(ConnectionMethods.OrganisationRentCollectionSettingsUpdated, (data: OrganisationRentCollectionSettings) => {
            console.log('OrganisationRentCollectionSettings update websocket event', data)
            this.organisationRentCollectionSettingsUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.GoCardlessRentDueRecordUpdated, (data: GoCardlessRentDueRecord) => {
            console.log('GoCardlessRentDueRecord update websocket event', data)
            this.goCardlessRentDueRecordUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.GoCardlessTenancyUpdated, (data: GoCardlessTenancy) => {
            console.log('GoCardlessTenancy update websocket event', data)
            this.goCardlessTenancyUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.RightmoveAuthUpdated, (data: RightmoveAuthenticationInformation) => {
            this.rightmoveAuthUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.RightmoveAuthDeleted, (data: RightmoveAuthenticationInformation) => {
            this.rightmoveAuthDeleted.next(data);
        });

        this._hubConnection.on(ConnectionMethods.RightmovePropertyUpdated, (data: RightmoveProperty) => {
            this.rightmovePropertyUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.RightmovePropertyDeleted, (data: RightmoveProperty) => {
            this.rightmovePropertyDeleted.next(data);
        });

        this._hubConnection.on(ConnectionMethods.YourPlaceListingUpdated, (data: YourPlaceListing) => {
            this.yourPlaceListingUpdated.next(data);
        });

        this._hubConnection.on(ConnectionMethods.YourPlaceListingDeleted, (data: YourPlaceListing) => {
            this.yourPlaceListingDeleted.next(data);
        });

        this._hubConnection.on(ConnectionMethods.NewLead, (data: Lead) => {
            this.newLead.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationArchivedForUser, data => {
            this.conversationArchivedForUser.next(data);
        });
        this._hubConnection.on(ConnectionMethods.ConversationRead, data => {
            this.conversationRead.next(data);
        });
        this._hubConnection.on(ConnectionMethods.ConversationReadForUser, data => {
            this.conversationReadForUser.next(data);
        });
        this._hubConnection.on(ConnectionMethods.ConversationMutedForUser, data => {
            this.conversationMutedForUser.next(data);
        });
        this._hubConnection.on(ConnectionMethods.ConversationDeleted, data => {
            this.conversationDeleted.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationAllowRepliesSwitched, data => {
            this.conversationAllowRepliesSwitched.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationNewUser, data => {
            this.conversationNewUser.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationDisabledUser, data => {
            this.conversationDisabledUser.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationAssigned, data => {
            this.conversationAssigned.next(data);
        });

        this._hubConnection.on(ConnectionMethods.ConversationNotificationState, data => {
            this.conversationNotificationState.next(data);
        });

        this._hubConnection.on(ConnectionMethods.Inventory, data => {
            this.inventoryEvent.next(data);
        });

        this._hubConnection.on(ConnectionMethods.GovHmrcAuth, data => {
            this.govHmrcAuth.next(data);
        });

        this._hubConnection.on(ConnectionMethods.RentMatchRuleUpdated, data => {
            this.rentMatchRuleUpdated.next(data);
        });
    }

    public onOperationInitiated(connectionMethod: string, objectId: string) {
        this.setFallbackTimeout(connectionMethod, objectId);
    }

    private setFallbackTimeout(connectionMethod: string, objectId: string) {
        this.fallbackTimeouts[connectionMethod] = setTimeout(() => {
            this.fallbackTimeoutSubject.next({ connectionMethod, triggered: true, objectId: objectId }); // Emit event when timeout occurs
        }, 5000); // 5 seconds timeout: if nothing is received within the 5 seconds after we got the Ok reponse
    }

    private clearFallbackTimeout(connectionMethod: string) {
        if (this.fallbackTimeouts[connectionMethod]) {
            clearTimeout(this.fallbackTimeouts[connectionMethod]);
            delete this.fallbackTimeouts[connectionMethod];
        }
    }
}
